import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Checkbox,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalLanguage } from '../../components/WhatsNew/useWhatsNew';
import {
  LANGUAGES,
  TEMPLATE_ROW_FIELDS,
  PICKABLE_CATEGORIES,
  type TemplateRow,
  type WorksheetCategory,
  worksheetLanguageArg,
} from './model';
import { printPathOf } from './printModel';
import { useWorksheetCommand } from './useWorksheetCommand';

/**
 * E34 · WorksheetTemplates — "Practice — Worksheets (library)" (cc-homework, chapter "Worksheets —
 * Templates & library (portal, E34)", screen e34e0009-0001-4000-8000-000000000005): the practice's
 * worksheets and the CopingCard library side by side, filterable by category and language; "Use this
 * template" copies a library one into the practice (the Builder opens on the copy). With the
 * "Practice — Worksheets — Retire (section)" (e34e0009-…-0017): Retire → confirm with a reason →
 * RetireWorksheetTemplate; a retired template leaves the list and the pickers, its submissions stay.
 *
 * LANGUAGE (founder 2026-10-02, GWTs e3450009-…-071/072): the page lists the portal language's templates by
 * default; the box "Show worksheets in all languages" (OFF) lists every language and shows the dropdown;
 * unticking hides it and resets its pick.
 *
 * Q-paper-2 A: a Print action on each row (the latest version) — only for a paper-eligible template, else disabled
 * with "Scored worksheets and consent forms are completed in the app." (worksheets.library.print, printNotEligible).
 *
 * Behavior lines implemented here: i18n worksheets.library.*; gesture Retire → confirm with reason →
 * RetireWorksheetTemplate, i18n worksheets.retire.*. The Assign drawer's worksheet picker reads the
 * same query (`worksheetTemplates`).
 */
type CategoryFilter = 'all' | WorksheetCategory;

export const WorksheetLibraryPage = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const { run } = useWorksheetCommand();
  const [category, setCategory] = useState<CategoryFilter>('all');
  const portalLanguage = usePortalLanguage();
  const [showAll, setShowAll] = useState(false);
  const [language, setLanguage] = useState<string | undefined>();
  const [retiring, setRetiring] = useState<TemplateRow | null>(null);
  const [reason, setReason] = useState('');
  const [retireProblem, setRetireProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, isFetching, isError, refetch } = useCustom<TemplateRow[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'worksheetTemplates',
      fields: TEMPLATE_ROW_FIELDS,
      variables: {
        category: { name: 'category', type: 'String', value: category },
        language: { name: 'language', type: 'String', value: worksheetLanguageArg(showAll, language, portalLanguage) },
      },
    },
  });
  const rows = useMemo(() => data?.data ?? [], [data]);

  /**
   * Q-paper-2 A — Print opens the printable rendering of the latest version. Offered only for a paper-eligible template
   * (unscored and not a consent); a scored worksheet or a consent shows Print disabled with the reason on hover.
   */
  const printButton = (r: TemplateRow) => (
    <Tooltip title={r.paperEligible ? undefined : t('worksheets.library.printNotEligible')}>
      <span data-testid={`worksheet-row-print-wrap-${r.templateId}`}>
        <Button
          disabled={!r.paperEligible}
          onClick={() => navigate(printPathOf(r.templateId))}
          data-testid={`worksheet-row-print-${r.templateId}`}
        >
          {t('worksheets.library.print')}
        </Button>
      </span>
    </Tooltip>
  );

  const retire = async () => {
    if (!retiring) return;
    if (!reason.trim()) {
      setRetireProblem(t('worksheets.retire.reasonRequired'));
      return;
    }
    setBusy(true);
    const r = await run(
      'retireWorksheetTemplate',
      { templateId: retiring.templateId, reason: reason.trim() },
      t('worksheets.retire.failed'),
    );
    setBusy(false);
    if (!r.ok) {
      setRetireProblem(r.message);
      return;
    }
    message.success(t('worksheets.retire.done'));
    setRetiring(null);
    refetch();
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="worksheet-library">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            {t('worksheets.library.title')}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('worksheets.library.intro')}
          </Typography.Paragraph>
        </div>
        <Space wrap>
          {/* block 5b — the founder's Q-b5-16 C: one click from the list to the import, always for a NEW template
              (the Builder opens with its Import from a file section; the Builder keeps its own header button) */}
          <Button
            icon={<UploadOutlined />}
            onClick={() => navigate('/worksheets/new?import=1')}
            data-testid="worksheet-library-import"
          >
            {t('worksheets.import.open')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/worksheets/new')}
            data-testid="worksheet-library-new"
          >
            {t('worksheets.library.new')}
          </Button>
        </Space>
      </Space>

      <Checkbox
        checked={showAll}
        onChange={(e) => {
          setShowAll(e.target.checked);
          if (!e.target.checked) setLanguage(undefined);
        }}
        data-testid="worksheet-library-all-languages"
      >
        {t('worksheets.library.allLanguages')}
      </Checkbox>

      <Space wrap>
        <Radio.Group
          optionType="button"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="worksheet-library-category"
        >
          <Radio.Button value="all" data-testid="worksheet-library-category-all">
            {t('worksheets.library.all')}
          </Radio.Button>
          {PICKABLE_CATEGORIES.map((c) => (
            <Radio.Button key={c} value={c} data-testid={`worksheet-library-category-${c}`}>
              {t(`worksheets.category.${c}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
        {showAll ? (
          <Select
            allowClear
            style={{ minWidth: 160 }}
            placeholder={t('worksheets.library.anyLanguage')}
            value={language}
            options={LANGUAGES.map((l) => ({ value: l, label: t(`worksheets.language.${l}`) }))}
            onChange={(v) => setLanguage(v)}
            data-testid="worksheet-library-language"
          />
        ) : null}
      </Space>

      {isError ? (
        <Alert type="error" showIcon message={t('worksheets.library.loadFailed')} />
      ) : null}

      <Table<TemplateRow>
        rowKey="templateId"
        loading={isFetching}
        dataSource={rows}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        locale={{ emptyText: t('worksheets.library.empty') }}
        onRow={(r) => ({ 'data-testid': `worksheet-row-${r.templateId}` } as never)}
        columns={[
          {
            title: t('worksheets.library.name'),
            dataIndex: 'name',
            render: (name: string, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong data-testid={`worksheet-row-name-${r.templateId}`}>
                  {name}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.fieldCount === 0
                    ? t('worksheets.library.noQuestions')
                    : t('worksheets.library.questions', { count: r.fieldCount })}
                  {' · '}
                  {t('worksheets.library.version', { version: r.version })}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t('worksheets.library.category'),
            dataIndex: 'category',
            render: (c: string, r) => (
              <Space size={4} wrap>
                <Tag data-testid={`worksheet-row-category-${r.templateId}`}>
                  {t(`worksheets.category.${c}`)}
                </Tag>
                {r.signatureRequired ? <Tag color="gold">{t('worksheets.library.signature')}</Tag> : null}
                {r.repeatable ? <Tag color="blue">{t('worksheets.library.repeatable')}</Tag> : null}
              </Space>
            ),
          },
          {
            title: t('worksheets.library.language'),
            dataIndex: 'language',
            render: (l: string) => t(`worksheets.language.${l}`),
          },
          {
            title: t('worksheets.library.owner'),
            dataIndex: 'owner',
            render: (o: string, r) => (
              <Tag
                color={o === 'library' ? 'green' : 'purple'}
                data-testid={`worksheet-row-owner-${r.templateId}`}
              >
                {t(`worksheets.library.owners.${o}`)}
              </Tag>
            ),
          },
          {
            title: t('worksheets.library.assigned'),
            dataIndex: 'assignedCount',
            render: (n: number, r) => (
              <span data-testid={`worksheet-row-assigned-${r.templateId}`}>{n}</span>
            ),
          },
          {
            title: '',
            key: 'actions',
            render: (_: unknown, r) =>
              r.owner === 'library' ? (
                <Space>
                  <Button
                    onClick={() => navigate(`/worksheets/new?from=${encodeURIComponent(r.templateId)}`)}
                    data-testid={`worksheet-row-use-${r.templateId}`}
                  >
                    {t('worksheets.library.use')}
                  </Button>
                  {printButton(r)}
                </Space>
              ) : (
                <Space>
                  <Button
                    onClick={() => navigate(`/worksheets/${encodeURIComponent(r.templateId)}/edit`)}
                    data-testid={`worksheet-row-edit-${r.templateId}`}
                  >
                    {t('worksheets.library.edit')}
                  </Button>
                  {printButton(r)}
                  <Button
                    danger
                    onClick={() => {
                      setRetiring(r);
                      setReason('');
                      setRetireProblem(null);
                    }}
                    data-testid={`worksheet-row-retire-${r.templateId}`}
                  >
                    {t('worksheets.retire.action')}
                  </Button>
                </Space>
              ),
          },
        ]}
      />

      <Modal
        open={!!retiring}
        title={t('worksheets.retire.title')}
        okText={t('worksheets.retire.confirm')}
        cancelText={t('worksheets.retire.cancel')}
        okButtonProps={{ danger: true, loading: busy, 'data-testid': 'worksheet-retire-confirm' } as never}
        onOk={retire}
        onCancel={() => setRetiring(null)}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Paragraph>
            {t('worksheets.retire.body', { name: retiring?.name ?? '' })}
          </Typography.Paragraph>
          <Input.TextArea
            value={reason}
            maxLength={1000}
            autoSize={{ minRows: 2, maxRows: 5 }}
            placeholder={t('worksheets.retire.reasonPlaceholder')}
            onChange={(e) => setReason(e.target.value)}
            data-testid="worksheet-retire-reason"
          />
          {retireProblem ? (
            <Alert type="error" showIcon message={retireProblem} data-testid="worksheet-retire-error" />
          ) : null}
        </Space>
      </Modal>
    </Space>
  );
};
